import React from 'react'
import { Link } from 'react-router-dom'
import { Calculator, Check } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const PricingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Calculator className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-6xl mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              Choose the plan that works best for your 1031 exchange needs.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <h3 className="text-2xl font-bold text-foreground mb-2">Basic</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">$999</span>
                <span className="text-muted-foreground">/exchange</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Qualified Intermediary Services</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Document Management</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Deadline Tracking</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Email Support</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openChat'))
                }}
              >
                Get Started
              </Button>
            </div>

            {/* Professional Plan */}
            <div className="bg-card p-8 rounded-2xl shadow-xl border-2 border-primary relative">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 rounded-bl-lg rounded-tr-2xl text-sm font-semibold">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">Professional</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">$1,999</span>
                <span className="text-muted-foreground">/exchange</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Everything in Basic</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>AI Assistant (Neela)</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Automated Form Generation</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Exchange Calculator</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Priority Support</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openChat'))
                }}
              >
                Get Started
              </Button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border">
              <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">Custom</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Everything in Professional</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Dedicated Account Manager</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Custom Integrations</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Volume Discounts</span>
                </li>
                <li className="flex items-center text-foreground">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>24/7 Phone Support</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  What's included in the base price?
                </h3>
                <p className="text-muted-foreground">
                  All plans include qualified intermediary services, document management, and deadline tracking. 
                  Higher tiers add AI assistance, automated forms, and enhanced support.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Can I upgrade or downgrade my plan?
                </h3>
                <p className="text-muted-foreground">
                  Yes, you can change your plan at any time. Changes will be reflected in your next exchange.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Are there any hidden fees?
                </h3>
                <p className="text-muted-foreground">
                  No hidden fees. The price you see is the price you pay. Additional services like rush processing 
                  may incur extra charges, which will be clearly disclosed upfront.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

